/**
 * BulkMisUpload Component
 *
 * Complete file upload functionality for bulk data fetch operations
 * Matches the design from upload_sidebar.png
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, CheckCircle, Info, Lightbulb, Download, X } from 'lucide-react';
import { Button, Drawer, DrawerContent, DrawerOverlay } from 'equal-ds-ui';
import { DatePicker } from 'equal-ds-ui';
import * as XLSX from 'xlsx';
import { bulkMisAPI } from '../../../services/bulk-mis-api.service';
import { validateFileContent as validateContentCentralized } from '../../../utils/bulk-upload-validators';
import { BULK_DATA_FETCH_CONFIG } from '../../../config/bulk-upload-configs';
import { isBulkOrchestrationV2Enabled } from '../../../utils/feature-flags';
import { toISTDayBoundaryISO } from '../../../utils/date';

interface BulkMisUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

export const BulkMisUpload: React.FC<BulkMisUploadProps> = ({ isOpen, onClose, onUploadSuccess }) => {
  const isV2 = isBulkOrchestrationV2Enabled();
  // Form state
  const [referenceId, setReferenceId] = useState('');
  const [referenceIdError, setReferenceIdError] = useState<string>('');
  const [fiStartDate, setFiStartDate] = useState<Date | null>(null);
  const [fiEndDate, setFiEndDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string>('');

  // File state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isValidFile, setIsValidFile] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortController = useRef<AbortController | null>(null);

  // Output required state
  const [outputType, setOutputType] = useState<'only_balance' | 'loan_monitoring'>('only_balance');
  const [ewsReportEnabled, setEwsReportEnabled] = useState(false);
  const [fullAnalyticsEnabled, setFullAnalyticsEnabled] = useState(false);

  // Duplicate reference ID dialog state
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [suggestedReferenceId, setSuggestedReferenceId] = useState<string | null>(null);

  // Feature flags from sessionStorage
  const [showBalanceOption, setShowBalanceOption] = useState(false);
  const [showLoanMonitoringOption, setShowLoanMonitoringOption] = useState(false);

  // Configuration
  const allowedFileTypes = ['.csv', '.json', '.xls', '.xlsx', '.xlsm', '.xlsb', '.ods'];
  const maxFileSizeInMB = sessionStorage.getItem('bulkPipelineUploadFileSizeLimitInMB')
    ? parseInt(sessionStorage.getItem('bulkPipelineUploadFileSizeLimitInMB') || '10')
    : 10;
  const maxRecordLimit = sessionStorage.getItem('bulkUploadMaxRecordLimit')
    ? parseInt(sessionStorage.getItem('bulkUploadMaxRecordLimit') || '10000')
    : 10000;

  // Close modal
  const closeModal = () => {
    onClose();
    resetForm();
  };

  // Reset form
  const resetForm = () => {
    setReferenceId('');
    setReferenceIdError('');
    setFiStartDate(null);
    setFiEndDate(null);
    setDateError('');
    setFileUploaded(false);
    setSelectedFile(null);
    setIsValidFile(false);
    setErrorMessages([]);
    setSubmissionSuccess(false);
    setShowDuplicateDialog(false);
    setSuggestedReferenceId(null);
    setEwsReportEnabled(false);
    setFullAnalyticsEnabled(false);

    // Reset output type based on available options
    if (showBalanceOption && !showLoanMonitoringOption) {
      setOutputType('only_balance');
    } else if (!showBalanceOption && showLoanMonitoringOption) {
      setOutputType('loan_monitoring');
    } else if (showBalanceOption && showLoanMonitoringOption) {
      setOutputType('only_balance'); // Default to balance when both available
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  // Initialize feature flags from sessionStorage
  React.useEffect(() => {
    const isBalanceDataIncluded = sessionStorage.getItem('isBalanceDataIncluded') === 'true';
    const isEWSEnabled = sessionStorage.getItem('isEWSEnabled') === 'true';

    setShowBalanceOption(isBalanceDataIncluded);
    setShowLoanMonitoringOption(isEWSEnabled);

    // Auto-select based on available options
    if (isBalanceDataIncluded && !isEWSEnabled) {
      // Only balance available
      setOutputType('only_balance');
    } else if (!isBalanceDataIncluded && isEWSEnabled) {
      // Only loan monitoring available
      setOutputType('loan_monitoring');
      setEwsReportEnabled(true);
    } else if (isBalanceDataIncluded && isEWSEnabled) {
      // Both available, default to balance
      setOutputType('only_balance');
    }
  }, []);

  // Reset form when drawer opens
  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Helper function to count records in a file
  const countRecordsInFile = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const reader = new FileReader();

      reader.onerror = () => reject(new Error('Failed to read file'));

      if (fileExtension === '.csv') {
        // Count CSV rows
        reader.onload = (event: any) => {
          try {
            const text = event.target.result as string;
            const lines = text.split(/\r\n|\n|\r/).filter(line => line.trim().length > 0);
            // Subtract 1 for header row
            const recordCount = Math.max(0, lines.length - 1);
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse CSV file'));
          }
        };
        reader.readAsText(file);
      } else if (fileExtension === '.json') {
        // Count JSON array items
        reader.onload = (event: any) => {
          try {
            const text = event.target.result as string;
            const jsonData = JSON.parse(text);
            const recordCount = Array.isArray(jsonData) ? jsonData.length : 0;
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse JSON file'));
          }
        };
        reader.readAsText(file);
      } else {
        // Count Excel rows using XLSX
        reader.onload = (event: any) => {
          try {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

            if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
              resolve(0);
              return;
            }

            // Get first sheet
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
            // Subtract 1 for header row
            const recordCount = Math.max(0, jsonData.length - 1);
            resolve(recordCount);
          } catch (error) {
            reject(new Error('Failed to parse Excel file'));
          }
        };
        reader.readAsArrayBuffer(file);
      }
    });
  };

  // File validation
  const validateFile = (file: File): Promise<string[]> => {
    return new Promise(async (resolve) => {
      const errors: string[] = [];

      // 1. Filename validation: only alphanumeric, hyphen, underscore, space, dot, parentheses
      const trimmedFileName = file.name.trim();
      const validFileNamePattern = /^[a-zA-Z0-9_.\- ()]+$/;
      if (!validFileNamePattern.test(trimmedFileName)) {
        errors.push('Invalid file name. Only alphanumeric, hyphen, underscore, space, dot, and parentheses are allowed');
      }

      // 2. File size validation
      if (file.size > maxFileSizeInMB * 1024 * 1024) {
        errors.push(`File size exceeds ${maxFileSizeInMB}MB limit`);
      }

      // 3. Record count validation
      try {
        const recordCount = await countRecordsInFile(file);
        if (recordCount > maxRecordLimit) {
          errors.push(`File contains ${recordCount.toLocaleString()} records. Maximum ${maxRecordLimit.toLocaleString()} records allowed`);
        }
      } catch (error) {
        // If counting fails, we'll catch it in the next validation step
        console.error('Error counting records:', error);
      }

      // 4. Content validation (consentId format, duplicates, dates) - using centralized validator
      try {
        const contentValidation = await validateContentCentralized(file, BULK_DATA_FETCH_CONFIG);
        errors.push(...contentValidation.errors);
      } catch (error) {
        console.error('Error validating file content:', error);
        errors.push('Failed to validate file content');
      }

      // 5. Check file extension
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === '.csv' || fileExtension === '.json') {
        // CSV and JSON validation is complete
        resolve(errors);
        return;
      }

      // 6. File corruption and password protection check using XLSX (for Excel files)
      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

          // Check for file corruption
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            errors.push('File is corrupted or empty');
          }
        } catch (error) {
          errors.push('File is password protected or corrupted');
        }
        resolve(errors);
      };

      reader.onerror = () => {
        errors.push('Failed to read file');
        resolve(errors);
      };

      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file selection
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setErrorMessages(['Please select a file']);
      setIsValidFile(false);
      setFileUploaded(false);
      return;
    }

    await processFile(file);
  };

  // Process file (shared between input and drag-drop)
  const processFile = async (file: File) => {
    // Check file extension
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedFileTypes.includes(fileExtension)) {
      setErrorMessages([`Invalid file type. Allowed types: ${allowedFileTypes.join(', ')}`]);
      setIsValidFile(false);
      setFileUploaded(false);
      setSelectedFile(null);
      return;
    }

    // Validate file
    const errors = await validateFile(file);

    if (errors.length > 0) {
      setErrorMessages(errors);
      setIsValidFile(false);
      setFileUploaded(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setIsValidFile(true);
      setUploadedFileName(file.name);
      setFileUploaded(true);
      setSelectedFile(file);
      setErrorMessages([]);
    }
  };

  // Remove file
  const removeFile = () => {
    setUploadedFileName('');
    setFileUploaded(false);
    setIsValidFile(false);
    setSelectedFile(null);
    setErrorMessages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Re-upload file (refresh)
  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isUploading) {
      setIsDragOver(true);
    }
  }, [isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (isUploading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  }, [isUploading]);

  // Submit file upload. overrideReferenceId is used when "Use Suggested" is clicked to avoid stale closure.
  const submitDetails = async (overrideReferenceId?: string) => {
    const effectiveReferenceId = overrideReferenceId || referenceId;

    // Validate required fields
    if (!effectiveReferenceId.trim()) {
      setErrorMessages(['Reference ID is required']);
      return;
    }

    // Check if there's a validation error (skip if using override from suggestion)
    if (referenceIdError && !overrideReferenceId) {
      setErrorMessages([referenceIdError]);
      return;
    }

    // Validate reference_id format (matches backend validation)
    if (isV2) {
      const referenceIdPattern = /^[a-zA-Z0-9_-][a-zA-Z0-9_ ()-]{4,69}$/;
      if (!referenceIdPattern.test(effectiveReferenceId.trim())) {
        setErrorMessages(['Reference ID must be 5-70 characters, starting with alphanumeric/underscore/hyphen']);
        return;
      }
    } else {
      const referenceIdPattern = /^[a-zA-Z0-9\-_]+$/;
      if (!referenceIdPattern.test(effectiveReferenceId.trim())) {
        setErrorMessages(['Reference ID must contain only alphanumeric characters, hyphens and underscores']);
        return;
      }
    }

    if (!fileUploaded || !isValidFile || !selectedFile) {
      setErrorMessages(['Please select a valid file before submitting']);
      return;
    }

    // Validate date range if both dates provided
    if (fiStartDate && fiEndDate && fiStartDate > fiEndDate) {
      setErrorMessages(['FI Start Date must be before or equal to FI End Date']);
      return;
    }

    try {
      setIsUploading(true);
      setSubmissionSuccess(false);
      setErrorMessages([]);

      // Check for duplicate reference ID before uploading (V2 only)
      if (isV2) {
        try {
          const checkResult = await bulkMisAPI.checkReferenceId(effectiveReferenceId.trim());
          if (checkResult.exists) {
            setIsUploading(false);
            setSuggestedReferenceId(checkResult.suggestion);
            setShowDuplicateDialog(true);
            return;
          }
        } catch (checkError) {
          // If check fails, proceed with upload — server will catch duplicates
          console.error('Reference ID check failed, proceeding with upload:', checkError);
        }
      }

      // Create abort controller for cancellation
      uploadAbortController.current = new AbortController();

      // Build FormData for multipart upload (required by backend busboy parser)
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('reference_id', effectiveReferenceId.trim());

      // Build global_config with optional fields AND output preferences
      const globalConfig: {
        fiDataRangeFrom?: string;
        fiDataRangeTo?: string;
        output_required?: string;
        reports?: string[];
        isBankingAnalysisEnabled?: boolean;
      } = {};

      // Add date ranges if provided, inclusive of BOTH the start and end calendar days in
      // IST (00:00:00.000 -> 23:59:59.999 IST), independent of the browser timezone — so
      // "20 Jun -> 21 Jul" fetches data across the whole of both 20 Jun and 21 Jul.
      if (fiStartDate) {
        globalConfig.fiDataRangeFrom = toISTDayBoundaryISO(fiStartDate, 'start');
      }
      if (fiEndDate) {
        globalConfig.fiDataRangeTo = toISTDayBoundaryISO(fiEndDate, 'end');
      }
      // Add output_required and reports only for V2
      if (isV2) {
        globalConfig.output_required = outputType; // 'only_balance' | 'loan_monitoring'

        const reports: string[] = [];
        if (ewsReportEnabled) reports.push('ews');
        if (fullAnalyticsEnabled) reports.push('analytics');
        globalConfig.reports = reports;
        globalConfig.isBankingAnalysisEnabled = fullAnalyticsEnabled;
      }

      // Always send global_config (at minimum with output preferences)
      formData.append('global_config', JSON.stringify(globalConfig));

      // Use the API service which handles token rotation
      await bulkMisAPI.uploadFile(formData, uploadAbortController.current.signal);

      setIsUploading(false);
      setSubmissionSuccess(true);

      // Refresh data after upload
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error: unknown) {
      setIsUploading(false);
      setSubmissionSuccess(false);

      // Handle different error types
      // httpClient rejects with ApiError { message, status, data }
      // raw axios rejects with AxiosError { response: { status, data } }
      const err = error as any;
      const isAbort = err?.name === 'AbortError' || err?.name === 'CanceledError';
      const status = err?.response?.status || err?.status;
      const responseData = err?.response?.data || err?.data;

      if (isAbort) {
        setErrorMessages(['File upload cancelled']);
      } else if (status === 409 || responseData?.errorCode === 'DuplicateReferenceId') {
        // Duplicate reference_id — show suggestion dialog if available
        const suggestion = responseData?.suggestion;
        if (suggestion) {
          setSuggestedReferenceId(suggestion);
          setShowDuplicateDialog(true);
        } else {
          setErrorMessages(['This Reference ID already exists. Please use a different one.']);
        }
      } else {
        const errorMsg = responseData?.errorMsg || responseData?.message
          || (error instanceof Error ? error.message : null)
          || err?.message
          || 'Failed to upload file. Please try again.';
        setErrorMessages([errorMsg]);
      }
    }
  };

  // Cancel upload
  const cancelUpload = () => {
    if (uploadAbortController.current) {
      uploadAbortController.current.abort();
      uploadAbortController.current = null;
    }
    setIsUploading(false);
    closeModal();
  };

  // Download sample template.
  // V2 (bulk-orchestration) is CSV-only — the file is handed to BOS, whose FI-fetch
  // workflow requires `consentId` and optionally reads per-row `fi_data_range_from/to`.
  // The legacy V1 path parses an xlsx workbook server-side, so serve the sample that
  // matches the client's active path to stay compatible with the previous version.
  const downloadSampleFile = () => {
    const link = document.createElement('a');
    const sampleFile = isV2 ? 'data-fetch-sample.csv' : 'data-fetch-sample.xlsx';
    // Use relative path (without leading slash) to work across all environments
    link.href = 'assets/' + sampleFile;
    link.download = sampleFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Check if form is valid for submission
  const isFormValid = referenceId.trim() && !referenceIdError && isValidFile && fileUploaded && !dateError;

  return (
    <>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="70%"
        className="border-border-default bg-gradient-to-b from-background-secondary to-primary-50"
      >
        <DrawerContent className="equal-ds-root flex flex-col flex-1 overflow-y-auto p-6 space-y-4">
          {/* Submission Success View */}
          {submissionSuccess ? (
            <div className="flex-1 flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-[#0F3340] mb-1">Upload Successful</p>
                <p className="text-sm mb-4" style={{ color: '#666D78' }}>Your batch has been uploaded successfully</p>
                <Button onClick={closeModal} variant="primary">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Tips for uploading */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#F7BA20' }} />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-[#0F3340] mb-1">Tips for uploading</h4>
                    <p className="text-xs mb-3" style={{ color: '#666D78' }}>
                      Ensure the file you upload has the correct layout. To get started quickly, you can download our template using the button below and add your details to it.
                    </p>
                    <button
                      onClick={downloadSampleFile}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white rounded-full hover:bg-gray-50 transition-colors"
                      style={{ color: '#0F3340', border: '1.5px solid #0F3340' }}
                    >
                      <Download className="w-4 h-4" />
                      Download Template
                    </button>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="space-y-3">
                {/* Reference ID */}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: '#666D78' }}>
                    Reference ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={referenceId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReferenceId(val);
                      if (!val) { setReferenceIdError(''); return; }
                      if (isV2) {
                        const pat = /^[a-zA-Z0-9_-][a-zA-Z0-9_ ()-]*$/;
                        if (!pat.test(val)) { setReferenceIdError('Must start with alphanumeric/underscore/hyphen. Spaces and parentheses allowed.'); return; }
                        if (val.length < 5) { setReferenceIdError('Must be at least 5 characters'); return; }
                        if (val.length > 70) { setReferenceIdError('Cannot exceed 70 characters'); return; }
                      } else {
                        const pat = /^[a-zA-Z0-9\-_]*$/;
                        if (!pat.test(val)) { setReferenceIdError('Only alphanumeric, hyphens and underscores allowed.'); return; }
                      }
                      setReferenceIdError('');
                    }}
                    placeholder="Enter reference ID (alphanumeric, _, -, spaces, parens)"
                    className={`w-full px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 ${
                      referenceIdError
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    disabled={isUploading}
                  />
                  {referenceIdError && (
                    <p className="text-xs text-red-600 mt-1">{referenceIdError}</p>
                  )}
                </div>

                {/* FI-Start-Date, FI-End-Date (FP-6933): batch-level FI date range — for the one-time balance fetch.
                    Loan Monitoring fetches follow the consent's periodic window (6-month retrospective floor → expiry,
                    ~5.5yr grant) and are managed automatically, so a manual range doesn't fit → grayed out + disabled. */}
                <div className={`grid grid-cols-2 gap-3 ${outputType === 'loan_monitoring' ? 'opacity-50 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#666D78' }}>
                      FI-Start-Date
                    </label>
                    <DatePicker
                      selectedDate={fiStartDate}
                      onChange={(date: Date | null) => {
                        setFiStartDate(date);
                        if (date && fiEndDate && date > fiEndDate) setDateError('FI-Start-Date cannot be greater than FI-End-Date');
                        else setDateError('');
                      }}
                      placeholder="dd/mm/yyyy"
                      disabled={isUploading || outputType === 'loan_monitoring'}
                      triggerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: '#666D78' }}>
                      FI-End-Date
                    </label>
                    <DatePicker
                      selectedDate={fiEndDate}
                      onChange={(date: Date | null) => {
                        setFiEndDate(date);
                        if (fiStartDate && date && fiStartDate > date) setDateError('FI-Start-Date cannot be greater than FI-End-Date');
                        else setDateError('');
                      }}
                      placeholder="dd/mm/yyyy"
                      disabled={isUploading || outputType === 'loan_monitoring'}
                      triggerClassName="w-full"
                    />
                  </div>
                </div>
                {dateError && (
                  <p className="text-xs text-red-600 mt-1">{dateError}</p>
                )}
              </div>

              {/* UPLOAD FILE */}
              <div>
                <label className="block text-sm font-bold text-[#0F3340] mb-2">
                  UPLOAD FILE
                </label>

                <div
                  onDragOver={!fileUploaded ? handleDragOver : undefined}
                  onDragLeave={!fileUploaded ? handleDragLeave : undefined}
                  onDrop={!fileUploaded ? handleDrop : undefined}
                  className={`
                    relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
                    ${fileUploaded
                      ? 'border-gray-300 bg-gray-50'
                      : isDragOver
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50'
                    }
                    ${!fileUploaded && isUploading ? 'opacity-50 cursor-not-allowed' : !fileUploaded ? 'cursor-pointer' : ''}
                  `}
                  onClick={() => !fileUploaded && !isUploading && fileInputRef.current?.click()}
                >
                  {fileUploaded ? (
                    <>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#E7EDF0' }}>
                        <CheckCircle className="w-5 h-5" style={{ color: '#2D8659' }} />
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: '#0F3340' }}>{uploadedFileName}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFile(); }}
                          className="p-1 rounded transition-colors"
                          style={{ color: '#919CAB' }}
                          title="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 ${isDragOver ? 'bg-blue-100' : ''}`} style={!isDragOver ? { backgroundColor: '#E7EDF0' } : {}}>
                        <Upload className={`w-5 h-5 ${isDragOver ? 'text-blue-500' : ''}`} style={!isDragOver ? { color: '#0F3340' } : {}} />
                      </div>
                      <p className="text-sm" style={{ color: '#0F3340' }}>
                        {isDragOver ? 'Drop your file here' : (
                          <>
                            <span className="font-semibold">Drag & Drop File Here</span>
                            {' '}or{' '}
                            <span className="font-semibold">Click to Upload File</span>
                          </>
                        )}
                      </p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={allowedFileTypes.join(',')}
                    onChange={handleFileSelect}
                    disabled={isUploading}
                    className="hidden"
                  />
                </div>

                {/* Error Messages */}
                {errorMessages.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    {errorMessages.map((error, index) => (
                      <p key={index} className="text-xs text-red-700">
                        {error}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {/* INSTRUCTIONS */}
              <div className="rounded-xl p-4" style={{ backgroundColor: '#E7EDF0' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 flex-shrink-0" style={{ color: '#666D78' }} />
                  <h4 className="text-xs font-bold" style={{ color: '#666D78' }}>INSTRUCTIONS</h4>
                </div>
                <div className="space-y-3 text-xs ml-6" style={{ color: '#666D78', lineHeight: '0.50rem' }}>
                  <p>Required fields: <span className="font-semibold">Consent ID, Fetch Parameters</span></p>
                  <p>Supported formats: <span className="font-semibold">CSV, JSON</span></p>
                  <p>Maximum records per file: <span className="font-semibold">{maxRecordLimit.toLocaleString()}</span></p>
                </div>
              </div>

              {/* Output Required Section - V2 only */}
              {isV2 && (showBalanceOption || showLoanMonitoringOption) && (
                <div>
                  <h4 className="text-sm font-semibold text-[#0F3340] mb-1">OUTPUT REQUIRED</h4>
                  <p className="text-xs mb-3" style={{ color: '#666D78' }}>Select the type of output required</p>

                  {/* Output Type Selection - Radio Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Only Balance Option */}
                    <label className={`flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors ${
                      outputType === 'only_balance' ? 'bg-white' : ''
                    }`} style={{ borderColor: outputType === 'only_balance' ? '#0F3340' : '#D1D5DB' }}>
                      <input
                        type="radio"
                        name="outputType"
                        value="only_balance"
                        checked={outputType === 'only_balance'}
                        onChange={(e) => { const val = e.target.value as 'only_balance' | 'loan_monitoring'; setOutputType(val); if (val === 'loan_monitoring') { setEwsReportEnabled(true); setFiStartDate(null); setFiEndDate(null); setDateError(''); } }}
                        disabled={isUploading}
                        className="mt-0.5 w-4 h-4"
                        style={{ accentColor: '#0F3340' }}
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-[#0F3340]">Only Balance</span>
                        <p className="text-xs mt-0.5" style={{ color: '#666D78' }}>Single report with balance information for all records</p>
                      </div>
                    </label>

                    {/* Loan Monitoring Data Option */}
                    <div className="relative group">
                      <label
                        className={`flex items-start gap-2 p-3 border-2 rounded-lg transition-colors ${
                          !showLoanMonitoringOption
                            ? 'cursor-not-allowed opacity-50'
                            : outputType === 'loan_monitoring'
                              ? 'cursor-pointer bg-white'
                              : 'cursor-pointer hover:bg-white'
                        }`}
                        style={{ borderColor: outputType === 'loan_monitoring' && showLoanMonitoringOption ? '#0F3340' : '#D1D5DB' }}
                      >
                        <input
                          type="radio"
                          name="outputType"
                          value="loan_monitoring"
                          checked={outputType === 'loan_monitoring'}
                          onChange={(e) => { const val = e.target.value as 'only_balance' | 'loan_monitoring'; setOutputType(val); if (val === 'loan_monitoring') { setEwsReportEnabled(true); setFiStartDate(null); setFiEndDate(null); setDateError(''); } }}
                          disabled={isUploading || !showLoanMonitoringOption}
                          className="mt-0.5 w-4 h-4"
                          style={{ accentColor: '#0F3340' }}
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-[#0F3340]">Loan Monitoring Data</span>
                          <p className="text-xs mt-0.5" style={{ color: '#666D78' }}>Detailed analytics with early warning signals</p>
                        </div>
                      </label>
                      {!showLoanMonitoringOption && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ backgroundColor: 'rgba(15, 51, 64, 0.85)' }}>
                          <span className="text-xs font-semibold text-white px-3 py-1.5 text-center">You don't have access to this feature</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Choose reports required - Only visible when Loan Monitoring is selected */}
                  {outputType === 'loan_monitoring' && (
                    <div className="mt-4">
                      <div className="flex items-center gap-4">
                        <span className="text-xs font-medium whitespace-nowrap" style={{ color: '#666D78' }}>Choose reports required:</span>
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          {/* EWS Report */}
                          <label className={`flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors ${
                            ewsReportEnabled ? 'bg-white' : ''
                          }`} style={{ borderColor: ewsReportEnabled ? '#0F3340' : '#D1D5DB' }}>
                            <input
                              type="checkbox"
                              checked={ewsReportEnabled}
                              onChange={(e) => setEwsReportEnabled(e.target.checked)}
                              disabled={isUploading}
                              className="mt-0.5 w-4 h-4 rounded"
                              style={{ accentColor: '#2D8659' }}
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-[#0F3340]">EWS Report</span>
                              <p className="text-xs mt-0.5" style={{ color: '#666D78' }}>Single file with all EWS parameters against each consent</p>
                            </div>
                          </label>
                          {/* Analytics Report */}
                          <label className={`flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-colors ${
                            fullAnalyticsEnabled ? 'bg-white' : ''
                          }`} style={{ borderColor: fullAnalyticsEnabled ? '#0F3340' : '#D1D5DB' }}>
                            <input
                              type="checkbox"
                              checked={fullAnalyticsEnabled}
                              onChange={(e) => setFullAnalyticsEnabled(e.target.checked)}
                              disabled={isUploading}
                              className="mt-0.5 w-4 h-4 rounded"
                              style={{ accentColor: '#2D8659' }}
                            />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-[#0F3340]">Analytics</span>
                              <p className="text-xs mt-0.5" style={{ color: '#666D78' }}>Multiple files with full analytics report for each consent</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </DrawerContent>

        {/* Footer - Fixed at bottom */}
        {!submissionSuccess && (
          <div className="flex-shrink-0 px-6 py-4 border-t" style={{ borderColor: '#E7EDF0' }}>
            <style>{`
              .equal-ds-root.equal-ds-root.equal-ds-root .bulk-upload-btn-disabled,
              .bulk-upload-btn-disabled.bulk-upload-btn-disabled.bulk-upload-btn-disabled {
                background-color: #E7EDF0 !important;
                color: #919CAB !important;
                border: 1px solid #D1D5DB !important;
                cursor: not-allowed !important;
                opacity: 1 !important;
              }
              .equal-ds-root.equal-ds-root.equal-ds-root .bulk-upload-btn-enabled,
              .bulk-upload-btn-enabled.bulk-upload-btn-enabled.bulk-upload-btn-enabled {
                background-color: #0F3340 !important;
                color: #FFFFFF !important;
                border: 1px solid #0F3340 !important;
                cursor: pointer !important;
                opacity: 1 !important;
              }
            `}</style>
            <button
              onClick={isUploading ? cancelUpload : () => submitDetails()}
              disabled={!isFormValid && !isUploading}
              className={`w-full py-3 rounded-lg text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors ${
                (!isFormValid && !isUploading) ? 'bulk-upload-btn-disabled' : 'bulk-upload-btn-enabled'
              }`}
            >
              {isUploading && (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {isUploading ? 'UPLOADING...' : 'UPLOAD BATCH'}
            </button>
          </div>
        )}
      </Drawer>

      {/* Upload Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg p-4 shadow-xl">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium text-[#0F3340]">Uploading file...</p>
          </div>
        </div>
      )}

      {/* Duplicate Reference ID Dialog - V2 only */}
      {isV2 && showDuplicateDialog && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-6 shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#0F3340] mb-2">Reference ID Already Exists</h3>
            <p className="text-sm text-gray-600 mb-4">
              The reference ID <span className="font-semibold">"{referenceId}"</span> is already in use.
              {suggestedReferenceId && (
                <> We suggest using <span className="font-semibold">"{suggestedReferenceId}"</span> instead.</>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDuplicateDialog(false);
                  setSuggestedReferenceId(null);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Edit Manually
              </button>
              {suggestedReferenceId && (
                <button
                  onClick={() => {
                    const suggested = suggestedReferenceId!;
                    setReferenceId(suggested);
                    setReferenceIdError('');
                    setShowDuplicateDialog(false);
                    setSuggestedReferenceId(null);
                    // Pass directly to avoid stale closure
                    submitDetails(suggested);
                  }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#0F3340' }}
                >
                  Use Suggested
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
